import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError, api } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** A team slot the target state needs and the ticket doesn't have filled. */
export type MissingSlot = { slot: string; label: string };

/** Ask the engine (dry run) whether this transition lacks someone in a team slot. */
export async function findMissingSlot(
  workItemId: string,
  actionId: string,
): Promise<MissingSlot | null> {
  try {
    await api.post(`/api/work-items/${workItemId}/actions`, { actionId, dryRun: true });
    return null;
  } catch (err) {
    const detail = err instanceof ApiError ? (err.detail as Record<string, unknown> | null) : null;
    return typeof detail?.missingSlot === "string"
      ? { slot: detail.missingSlot, label: String(detail.missingSlotLabel ?? detail.missingSlot) }
      : null;
  }
}

type Candidate = { id: string; name: string | null };

/** Picker over the members that may sit in the slot (the server's list, by role). */
export function SlotPicker({
  workItemId,
  missing,
  value,
  onChange,
}: {
  workItemId: string;
  missing: MissingSlot;
  value: string;
  onChange: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<Candidate[] | null>(null);
  useEffect(() => {
    let live = true;
    api
      .get<{ users: Candidate[] }>(
        `/api/work-items/${workItemId}/slot-candidates?slot=${encodeURIComponent(missing.slot)}`,
      )
      .then((r) => live && setUsers(r.users))
      .catch(() => live && setUsers([]));
    return () => {
      live = false;
    };
  }, [workItemId, missing.slot]);

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium block">{missing.label}</span>
      <p className="text-xs text-muted-foreground">{t("wiSlot.nobody")}</p>
      <Combobox
        aria-label={missing.label}
        value={value}
        onChange={onChange}
        placeholder={users === null ? t("wiSlot.loading") : t("wiSlot.selectMember")}
        emptyLabel={t("wiSlot.noMemberHoldsRole")}
        options={(users ?? []).map((u) => ({ value: u.id, label: u.name ?? u.id }))}
      />
    </div>
  );
}

/**
 * Promise-style prompt for callers without their own dialog: resolves `{}`
 * when nothing is missing, the picked team when someone was chosen, or null
 * when the person cancelled.
 */
export function useTeamPrompt() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<{ workItemId: string; missing: MissingSlot } | null>(null);
  const [value, setValue] = useState("");
  const resolver = useRef<((team: Record<string, string> | null) => void) | null>(null);

  async function ask(workItemId: string, actionId: string) {
    const missing = await findMissingSlot(workItemId, actionId);
    if (!missing) return {};
    setValue("");
    setOpen({ workItemId, missing });
    return new Promise<Record<string, string> | null>((resolve) => {
      resolver.current = resolve;
    });
  }

  const finish = (team: Record<string, string> | null) => {
    resolver.current?.(team);
    resolver.current = null;
    setOpen(null);
  };

  const dialog = (
    <Dialog open={!!open} onOpenChange={(o) => !o && finish(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("wiSlot.whoTakesNext")}</DialogTitle>
        </DialogHeader>
        {open && (
          <SlotPicker
            workItemId={open.workItemId}
            missing={open.missing}
            value={value}
            onChange={setValue}
          />
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => finish(null)}>
            {t("wiSlot.cancel")}
          </Button>
          <Button
            size="sm"
            disabled={!value}
            onClick={() => open && finish({ [open.missing.slot]: value })}
          >
            {t("wiSlot.continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { ask, dialog };
}
